import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { cn, getInitials } from '../lib/utils';
import { 
  Calendar, Clock, ChevronLeft, ChevronRight, Plus, Settings, 
  Check, X, Loader2, Phone, User, FileText, MapPin, RefreshCw,
  CheckCircle, XCircle, AlertCircle, History
} from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Badge Component
const StatusBadge = ({ status, lang }) => {
  const tr = t(lang);
  const config = {
    pending: { label: tr('pending'), color: 'bg-yellow-500/20 text-yellow-500', icon: AlertCircle },
    confirmed: { label: tr('confirmed'), color: 'bg-green-500/20 text-green-500', icon: CheckCircle },
    completed: { label: tr('completed'), color: 'bg-blue-500/20 text-blue-500', icon: Check },
    cancelled: { label: tr('cancelled'), color: 'bg-red-500/20 text-red-500', icon: XCircle },
    rejected: { label: tr('rejected'), color: 'bg-red-500/20 text-red-500', icon: X },
  };
  
  const { label, color, icon: Icon } = config[status] || config.pending;
  
  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium", color)}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Appointment Card Component
const AppointmentCard = ({ appointment, isProvider, onStatusChange, onRefresh, lang }) => {
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const tr = t(lang);
  
  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await axios.put(`${API}/appointments/${appointment.id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      const messages = {
        confirmed: tr('appointmentConfirmed'),
        rejected: tr('appointmentRejected'),
        completed: tr('appointmentUpdated')
      };
      toast.success(messages[newStatus] || tr('appointmentUpdated'));
      onRefresh?.();
    } catch (error) {
      toast.error(tr('error'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = async () => {
    if (!window.confirm(tr('confirmCancel'))) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/appointments/${appointment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(tr('appointmentCancelled'));
      onRefresh?.();
    } catch (error) {
      toast.error(tr('error'));
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const localeMap = { en: 'en-US', ht: 'ht-HT', fr: 'fr-FR', es: 'es-ES' };
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(localeMap[lang] || 'en-US', options);
  };
  
  const person = isProvider 
    ? { name: appointment.client_display_name || appointment.client_username, avatar: appointment.client_avatar }
    : { name: appointment.provider_display_name || appointment.provider_username, avatar: appointment.provider_avatar };
  
  return (
    <div 
      className="bg-card border rounded-xl p-4 hover:shadow-md transition-all"
      data-testid={`appointment-card-${appointment.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={person.avatar} />
            <AvatarFallback className="bg-gradient-to-br from-pink-500/20 to-violet-500/20">
              {getInitials(person.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{person.name}</p>
            <p className="text-xs text-muted-foreground">{appointment.service_name}</p>
          </div>
        </div>
        <StatusBadge status={appointment.status} lang={lang} />
      </div>
      
      {/* Details */}
      <div className="space-y-2 text-sm mb-3">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(appointment.date)}</span>
          <Clock className="w-4 h-4 ml-2" />
          <span>{appointment.time}</span>
        </div>
        
        {isProvider && (
          <>
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="w-4 h-4" />
              <span>{appointment.client_name}</span>
            </div>
            {appointment.client_email && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>{appointment.client_email}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="w-4 h-4" />
              <span>{appointment.client_phone}</span>
            </div>
          </>
        )}
        
        {appointment.notes && (
          <div className="flex items-start gap-2 text-muted-foreground">
            <FileText className="w-4 h-4 mt-0.5" />
            <span className="line-clamp-2">{appointment.notes}</span>
          </div>
        )}
      </div>
      
      {/* Price */}
      <div className="flex items-center justify-between py-2 border-t">
        <span className="text-sm text-muted-foreground">{tr('price')}:</span>
        <span className={cn(
          "font-bold",
          appointment.service_is_free ? "text-green-500" : "text-pink-500"
        )}>
          {appointment.service_is_free ? tr('free') : `$${appointment.service_price}`}
        </span>
      </div>
      
      {/* Actions */}
      {appointment.status === 'pending' && (
        <div className="flex gap-2 mt-3">
          {isProvider ? (
            <>
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 text-red-500 border-red-500/50 hover:bg-red-500/10"
                onClick={() => handleStatusChange('rejected')}
                disabled={loading}
              >
                <X className="w-4 h-4 mr-1" />
                {tr('reject')}
              </Button>
              <Button 
                size="sm" 
                className="flex-1 bg-green-500 hover:bg-green-600"
                onClick={() => handleStatusChange('confirmed')}
                disabled={loading}
              >
                <Check className="w-4 h-4 mr-1" />
                {tr('accept')}
              </Button>
            </>
          ) : (
            <Button 
              size="sm" 
              variant="outline" 
              className="flex-1 text-red-500"
              onClick={handleCancel}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-1" />
              {tr('cancel')}
            </Button>
          )}
        </div>
      )}
      
      {appointment.status === 'confirmed' && isProvider && (
        <div className="flex gap-2 mt-3">
          <Button 
            size="sm" 
            variant="outline" 
            className="flex-1"
            onClick={() => handleStatusChange('completed')}
            disabled={loading}
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            {tr('markComplete')}
          </Button>
        </div>
      )}
    </div>
  );
};

// Empty State Component
const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-12">
    <div className="w-16 h-16 rounded-full bg-secondary/50 flex items-center justify-center mx-auto mb-4">
      <Icon className="w-8 h-8 text-muted-foreground" />
    </div>
    <h3 className="font-medium mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground">{description}</p>
  </div>
);

// Main Page Component
export default function AppointmentsPage() {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('provider');
  const [providerAppointments, setProviderAppointments] = useState([]);
  const [clientAppointments, setClientAppointments] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, completed
  
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch as provider
      const providerRes = await axios.get(`${API}/appointments/my?role=provider`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviderAppointments(providerRes.data.appointments || []);
      
      // Fetch as client
      const clientRes = await axios.get(`${API}/appointments/my?role=client`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientAppointments(clientRes.data.appointments || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error(tr('error'));
    } finally {
      setLoading(false);
    }
  }, [token, tr]);
  
  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);
  
  const filterAppointments = (appointments) => {
    if (filter === 'all') return appointments;
    return appointments.filter(a => a.status === filter);
  };
  
  const getStats = (appointments) => {
    return {
      pending: appointments.filter(a => a.status === 'pending').length,
      confirmed: appointments.filter(a => a.status === 'confirmed').length,
      completed: appointments.filter(a => a.status === 'completed').length,
    };
  };
  
  const providerStats = getStats(providerAppointments);
  const clientStats = getStats(clientAppointments);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{tr('appointments')}</h1>
            <p className="text-sm text-muted-foreground">{tr('manageAppointments')}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={fetchAppointments} className="rounded-full">
            <RefreshCw className={cn("w-5 h-5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>
      
      <div className="max-w-2xl mx-auto p-4">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="provider" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {tr('myServices')}
              {providerStats.pending > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-pink-500 text-white text-xs rounded-full">
                  {providerStats.pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="client" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {tr('myAppointments')}
              {clientStats.pending > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-pink-500 text-white text-xs rounded-full">
                  {clientStats.pending}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Filter Buttons */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {['all', 'pending', 'confirmed', 'completed'].map(f => (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(f)}
                className="rounded-full whitespace-nowrap"
              >
                {tr(f)}
              </Button>
            ))}
          </div>
          
          {/* Provider Tab Content */}
          <TabsContent value="provider" className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
              </div>
            ) : filterAppointments(providerAppointments).length === 0 ? (
              <EmptyState 
                icon={Calendar}
                title={tr('noAppointments')}
                description={tr('whenClientsBook')}
              />
            ) : (
              <div className="space-y-3">
                {filterAppointments(providerAppointments).map(apt => (
                  <AppointmentCard 
                    key={apt.id} 
                    appointment={apt} 
                    isProvider={true}
                    onRefresh={fetchAppointments}
                    lang={language}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Client Tab Content */}
          <TabsContent value="client" className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
              </div>
            ) : filterAppointments(clientAppointments).length === 0 ? (
              <EmptyState 
                icon={History}
                title={tr('noAppointments')}
                description={tr('whenYouBook')}
              />
            ) : (
              <div className="space-y-3">
                {filterAppointments(clientAppointments).map(apt => (
                  <AppointmentCard 
                    key={apt.id} 
                    appointment={apt} 
                    isProvider={false}
                    onRefresh={fetchAppointments}
                    lang={language}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="bg-yellow-500/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-yellow-500">
              {activeTab === 'provider' ? providerStats.pending : clientStats.pending}
            </p>
            <p className="text-xs text-muted-foreground">{tr('pending')}</p>
          </div>
          <div className="bg-green-500/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-green-500">
              {activeTab === 'provider' ? providerStats.confirmed : clientStats.confirmed}
            </p>
            <p className="text-xs text-muted-foreground">{tr('confirmed')}</p>
          </div>
          <div className="bg-blue-500/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-blue-500">
              {activeTab === 'provider' ? providerStats.completed : clientStats.completed}
            </p>
            <p className="text-xs text-muted-foreground">{tr('completed')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
